import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  TextField,
  Typography,
  CircularProgress,
  IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import moment from 'moment';
import DeleteDialog from '../../components/DeleteDialog';
import CommonAgGrid from '../../components/CommonAgGrid/CommonAgGrid';
import { useDispatch, useSelector } from 'react-redux';
import {
  getUnavailabilityByDoctor,
  addFullDayUnavailability,
  addCustomSlotUnavailability,
  addWeeklyOff,
  removeFullDayUnavailability,
  removeCustomSlotUnavailability,
  removeWeeklyOff,
  getDoctorUnavailability,
} from '../../apis/doctorUnavailabilitySlice';
import { toast } from 'react-toastify';
import { getDoctors } from '../../apis/doctorSlice';
import SearchClinic from '../../components/Autocomplete/SearchClinic';
import SearchDoctor from '../../components/Autocomplete/SearchDoctor';
import styles from './DoctorUnavailability.module.scss';

export default function DoctorUnavailability() {
  const dispatch = useDispatch();
  const { loggedIn } = useSelector((state) => state.authData || {});

  const { doctors } = useSelector((state) => state.doctorData || {});

  const { unavailability, unavailabilityLoading } = useSelector(
    (state) => state.unavailabilityData || {},
  );

  const [tabValue, setTabValue] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('fullDay');
  // delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteInfo, setDeleteInfo] = useState(null);

  // Form States
  const [fullDayDate, setFullDayDate] = useState(''); // "2025-12-04"
  const [fullDayReason, setFullDayReason] = useState('');

  const [customSlotDate, setCustomSlotDate] = useState('');
  const [customSlotStartTime, setCustomSlotStartTime] = useState('09:00');
  const [customSlotEndTime, setCustomSlotEndTime] = useState('10:00');
  const [doctorData, setDoctorData] = useState({
    clinicId: null,
    doctorId: null,
  });

  const [allUnavailability, setAllUnavailability] = useState([]);
  const [selectedClinicFilter, setSelectedClinicFilter] = useState(null);
  const [fullDayPagination, setFullDayPagination] = useState({ page: 0, pageSize: 5 });
  const [slotPagination, setSlotPagination] = useState({ page: 0, pageSize: 5 });

  const today = moment().format('YYYY-MM-DD');

  useEffect(() => {
    dispatch(getDoctors());
    // Load unavailability entries
    loadUnavailability();
  }, [dispatch]);

  // Auto-fill clinic for Admin (role A) and doctor for Doctor (role D)
  useEffect(() => {
    if (loggedIn) {
      if (loggedIn.role === 'A' && loggedIn.clinicId) {
        // Auto-fill clinic for Admin - format it as an object with label for display
        const clinicValue =
          typeof loggedIn.clinicId === 'object'
            ? {
                label: loggedIn.clinicId.name,
                value: loggedIn.clinicId._id,
                _id: loggedIn.clinicId._id,
                name: loggedIn.clinicId.name,
              }
            : loggedIn.clinicId;

        setDoctorData((prev) => ({
          ...prev,
          clinicId: clinicValue,
        }));
      } else if (loggedIn.role === 'D' && loggedIn.doctorId) {
        const clinicValue =
          typeof loggedIn.clinicId === 'object'
            ? {
                label: loggedIn.clinicId.name,
                value: loggedIn.clinicId._id,
                _id: loggedIn.clinicId._id,
                name: loggedIn.clinicId.name,
              }
            : loggedIn.clinicId;

        setDoctorData((prev) => ({
          ...prev,
          clinicId: clinicValue,
        }));
        // Auto-fill doctor for Doctor - format it properly for SearchDoctor
        const doctorValue =
          typeof loggedIn.doctorId === 'object'
            ? {
                label: loggedIn.doctorId.name,
                value: loggedIn.doctorId._id,
                name: loggedIn.doctorId.name,
                _id: loggedIn.doctorId._id,
              }
            : loggedIn.doctorId;

        setDoctorData((prev) => ({
          ...prev,
          doctorId: doctorValue,
        }));
      }
    }
  }, [loggedIn]);

  const loadUnavailability = async () => {
    const result = await dispatch(getDoctorUnavailability({}));
    if (result.type?.includes('fulfilled')) {
      const data = result.payload?.data;
      setAllUnavailability(Array.isArray(data) ? data : data ? [data] : []);
    }
  };

  const handleTabChange = (event, newValue) => setTabValue(newValue);

  const handleOpenDialog = (type) => {
    setDialogType(type);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFullDayDate('');
    setFullDayReason('');
    setCustomSlotDate('');
    setCustomSlotStartTime('09:00');
    setCustomSlotEndTime('10:00');

    // Reset to auto-filled values based on user role
    const resetData = {
      clinicId: null,
      doctorId: null,
    };

    if (loggedIn) {
      if (loggedIn.role === 'A' && loggedIn.clinicId) {
        // Format clinic for SearchClinic with label for display
        resetData.clinicId =
          typeof loggedIn.clinicId === 'object'
            ? {
                label: loggedIn.clinicId.name,
                value: loggedIn.clinicId._id,
                _id: loggedIn.clinicId._id,
                name: loggedIn.clinicId.name,
              }
            : loggedIn.clinicId;
      } else if (loggedIn.role === 'D' && loggedIn.doctorId) {
        // Format doctor for SearchDoctor
        resetData.doctorId =
          typeof loggedIn.doctorId === 'object'
            ? {
                label: loggedIn.doctorId.name,
                value: loggedIn.doctorId._id,
                name: loggedIn.doctorId.name,
                _id: loggedIn.doctorId._id,
              }
            : loggedIn.doctorId;
      }
    }

    setDoctorData(resetData);
  };

  // ADD FULL DAY OFF
  const handleAddFullDay = async () => {
    if (!fullDayDate) {
      toast.error('Please select a date');
      return;
    }

    if (moment(fullDayDate).isBefore(moment(), 'day')) {
      toast.error('Past dates are not allowed');
      return;
    }

    if (!doctorData.clinicId) {
      toast.error('Please select a clinic');
      return;
    }
    if (!doctorData.doctorId) {
      toast.error('Please select a doctor');
      return;
    }

    const payload = {
      clinicId: doctorData.clinicId,
      doctorId: doctorData.doctorId.value || doctorData.doctorId._id,
      date: fullDayDate,
      reason: fullDayReason || 'Doctor not available',
    };

    const result = await dispatch(addFullDayUnavailability(payload));
    if (result.type?.includes('fulfilled')) {
      toast.success('Full day off added');
      loadUnavailability();
      handleCloseDialog();
    } else {
      toast.error(result.payload?.message || 'Failed');
    }
  };

  // ADD CUSTOM SLOT
  const handleAddCustomSlot = async () => {
    if (!customSlotDate || !customSlotStartTime || !customSlotEndTime) {
      toast.error('Please fill all fields');
      return;
    }
    if (moment(fullDayDate).isBefore(moment(), 'day')) {
      toast.error('Past dates are not allowed');
      return;
    }

    if (customSlotStartTime >= customSlotEndTime) {
      toast.error('End time must be after start time');
      return;
    }
    if (!doctorData.clinicId) {
      toast.error('Please select a clinic');
      return;
    }
    if (!doctorData.doctorId) {
      toast.error('Please select a doctor');
      return;
    }

    const payload = {
      clinicId: doctorData.clinicId,
      doctorId: doctorData.doctorId.value || doctorData.doctorId._id,
      date: customSlotDate,
      slots: [
        {
          startTime: customSlotStartTime,
          endTime: customSlotEndTime,
        },
      ],
    };

    const result = await dispatch(addCustomSlotUnavailability(payload));
    if (result.type?.includes('fulfilled')) {
      toast.success('Custom slot added');
      loadUnavailability();
      handleCloseDialog();
    } else {
      toast.error(result.payload?.message || 'Failed');
    }
  };

  // ADD WEEKLY OFF
  //   const handleAddWeeklyOff = async () => {
  //     if (selectedWeeklyOffs.length === 0) {
  //       toast.error('Select at least one day');
  //       return;
  //     }

  //     const payload = { doctorId, weeklyOff: selectedWeeklyOffs };
  //     const result = await dispatch(addWeeklyOff(payload));
  //     if (result.type?.includes('fulfilled')) {
  //       toast.success('Weekly off updated');
  //       handleCloseDialog();
  //     } else {
  //       toast.error(result.payload?.message || 'Failed');
  //     }
  //   };

  // REMOVE FULL DAY
  const handleRemoveFullDay = async (unavailabilityItem, dateString) => {
    const payload = {
      doctorId: unavailabilityItem.doctorId._id || unavailabilityItem.doctorId,
      date: dateString,
    };
    const result = await dispatch(removeFullDayUnavailability(payload));
    if (result.type?.includes('fulfilled')) {
      toast.success('Removed');
      loadUnavailability();
    } else {
      toast.error('Failed to remove');
    }
  };

  // delete dialog helpers
  const openDeleteDialog = (info) => {
    setDeleteInfo(info);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeleteInfo(null);
  };

  const confirmDelete = async () => {
    if (deleteInfo) {
      const { type, unavail, date, startTime, endTime } = deleteInfo;
      if (type === 'fullDay') await handleRemoveFullDay(unavail, date);
      else if (type === 'customSlot')
        await handleRemoveCustomSlot(unavail, date, startTime, endTime);
    }
    closeDeleteDialog();
  };

  // REMOVE CUSTOM SLOT
  const handleRemoveCustomSlot = async (unavailabilityItem, dateString, startTime, endTime) => {
    const payload = {
      doctorId: unavailabilityItem.doctorId._id || unavailabilityItem.doctorId,
      date: dateString,
      startTime,
      endTime,
    };
    const result = await dispatch(removeCustomSlotUnavailability(payload));
    if (result.type?.includes('fulfilled')) {
      toast.success('Slot removed');
      loadUnavailability();
    } else {
      toast.error('Failed');
    }
  };

  //   // REMOVE WEEKLY OFF
  //   const handleRemoveWeeklyOff = async (day) => {
  //     const payload = { doctorId, day };
  //     const result = await dispatch(removeWeeklyOff(payload));
  //     if (result.type?.includes('fulfilled')) {
  //       toast.success('Weekly off removed');
  //     } else {
  //       toast.error('Failed');
  //     }
  //   };

  //   const handleWeeklyOffChange = (day) => {
  //     setSelectedWeeklyOffs(prev =>
  //       prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
  //     );
  //   };

  const handleSave = () => {
    if (dialogType === 'fullDay') handleAddFullDay();
    else if (dialogType === 'customSlot') handleAddCustomSlot();
    // else if (dialogType === 'weeklyOff') handleAddWeeklyOff();
  };

  const fullDayRows = useMemo(
    () =>
      allUnavailability
        .flatMap(
          (unavail) =>
            unavail.fullDayDates?.map((item, i) => ({
              id: `${unavail._id}-${i}`,
              clinic: unavail.clinicId?.name || '—',
              doctor: unavail.doctorId?.name || '—',
              date: moment(item.date).format('DD/MM/YYYY'),
              reason: item.reason || '—',
              unavail,
              rawDate: item.date,
            })) || [],
        )
        .filter((r) =>
          selectedClinicFilter
            ? r.clinic === (selectedClinicFilter.name || selectedClinicFilter.label)
            : true,
        ),
    [allUnavailability, selectedClinicFilter],
  );

  const customSlotRows = useMemo(
    () =>
      allUnavailability
        .flatMap(
          (unavail) =>
            unavail.customSlots?.flatMap((dateGroup) =>
              dateGroup.slots.map((slot, idx) => ({
                id: `${unavail._id}-${dateGroup.date}-${idx}`,
                clinic: unavail.clinicId?.name || '—',
                doctor: unavail.doctorId?.name || '—',
                date: moment(dateGroup.date).format('DD/MM/YYYY'),
                slot: `${slot.startTime} - ${slot.endTime}`,
                startTime: slot.startTime,
                endTime: slot.endTime,
                unavail,
                rawDate: dateGroup.date,
              })),
            ) || [],
        )
        .filter((r) =>
          selectedClinicFilter
            ? r.clinic === (selectedClinicFilter.name || selectedClinicFilter.label)
            : true,
        ),
    [allUnavailability, selectedClinicFilter],
  );

  const fullDayColumns = useMemo(
    () => [
      { field: 'clinic', headerName: 'Clinic', flex: 1, minWidth: 110 },
      { field: 'doctor', headerName: 'Doctor', flex: 1, minWidth: 120 },
      { field: 'date', headerName: 'Date', flex: 0.8, minWidth: 100 },
      { field: 'reason', headerName: 'Reason', flex: 1.6, minWidth: 180 },
      {
        colId: 'action',
        headerName: 'Action',
        width: 92,
        flex: 0,
        minWidth: 92,
        maxWidth: 92,
        sortable: false,
        pinned: 'right',
        cellRenderer: (params) => (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <IconButton
              color="error"
              size="small"
              onClick={() =>
                openDeleteDialog({
                  type: 'fullDay',
                  unavail: params.data.unavail,
                  date: params.data.rawDate,
                })
              }
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ),
      },
    ],
    [],
  );

  const customSlotColumns = useMemo(
    () => [
      { field: 'clinic', headerName: 'Clinic', flex: 1, minWidth: 110 },
      { field: 'doctor', headerName: 'Doctor', flex: 1, minWidth: 120 },
      { field: 'date', headerName: 'Date', flex: 0.8, minWidth: 100 },
      { field: 'slot', headerName: 'Unavailable Slot', flex: 1.6, minWidth: 170 },
      {
        colId: 'action',
        headerName: 'Action',
        width: 92,
        flex: 0,
        minWidth: 92,
        maxWidth: 92,
        sortable: false,
        pinned: 'right',
        cellRenderer: (params) => (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <IconButton
              color="error"
              size="small"
              onClick={() =>
                openDeleteDialog({
                  type: 'customSlot',
                  unavail: params.data.unavail,
                  date: params.data.rawDate,
                  startTime: params.data.startTime,
                  endTime: params.data.endTime,
                })
              }
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ),
      },
    ],
    [],
  );

  const isInitialLoading = unavailabilityLoading && allUnavailability.length === 0;

  return (
    <Box className={styles.page}>
      {isInitialLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
      <Box className={styles.pageHead}>
        <Typography className={styles.pageTitle}>Doctor Unavailability Management</Typography>
      </Box>

      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        className={styles.tabs}
        sx={{ borderBottom: 1, borderColor: '#d7e2ef', mb: 1.2 }}
      >
        <Tab label="Full Day Off" />
        <Tab label="Custom Slots" />
      </Tabs>

      {/* === FULL DAY OFF TAB === */}
      {tabValue === 0 && (
        <Card className={styles.tableCard}>
          <Box className={styles.tableHeader}>
            <Typography className={styles.tableTitle}>Full Day Off</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('fullDay')}
              className={styles.addBtn}
              size="small"
          >
            Add Full Day Off
          </Button>
          </Box>

          <CommonAgGrid
            rowData={fullDayRows}
            columnDefs={fullDayColumns}
            loading={unavailabilityLoading}
            height={280}
            pagination
            rowCount={fullDayRows.length}
            paginationModel={fullDayPagination}
            onPaginationModelChange={setFullDayPagination}
            pageSizeOptions={[5, 10, 25]}
            noRowsOverlayComponent={() => (
              <Typography sx={{ p: 2, color: '#5d748f', fontSize: '0.85rem' }}>
                No full day off entries
              </Typography>
            )}
          />
        </Card>
      )}

      {/* === CUSTOM SLOTS TAB === */}
      {tabValue === 1 && (
        <Card className={styles.tableCard}>
          <Box className={styles.tableHeader}>
            <Typography className={styles.tableTitle}>Custom Unavailable Slots</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('customSlot')}
              className={styles.addBtn}
              size="small"
          >
            Add Custom Slot
          </Button>
          </Box>

          <CommonAgGrid
            rowData={customSlotRows}
            columnDefs={customSlotColumns}
            loading={unavailabilityLoading}
            height={280}
            pagination
            rowCount={customSlotRows.length}
            paginationModel={slotPagination}
            onPaginationModelChange={setSlotPagination}
            pageSizeOptions={[5, 10, 25]}
            noRowsOverlayComponent={() => (
              <Typography sx={{ p: 2, color: '#5d748f', fontSize: '0.85rem' }}>
                No custom unavailable slots
              </Typography>
            )}
          />
        </Card>
      )}

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2, border: '1px solid #d7e2ef' } }}
      >
        <DialogTitle className={styles.dialogTitle}>
          {dialogType === 'fullDay' && 'Add Full Day Off'}
          {dialogType === 'customSlot' && 'Add Custom Unavailable Slot'}
        </DialogTitle>
        <DialogContent className={styles.dialogContent}>
          <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 1.15 }}>
            {/* Clinic and Doctor Selection - Always shown in dialog */}
            <SearchClinic
              open={true}
              setData={setDoctorData}
              data={doctorData}
              name="clinicId"
              label="Select Clinic"
              disabled={loggedIn?.role === 'A'}
            />
            <SearchDoctor
              open={true}
              setData={setDoctorData}
              data={doctorData}
              name="doctorId"
              label="Select Doctor"
              clinicId={doctorData.clinicId}
              disabled={loggedIn?.role === 'D'}
            />

            {/* Full Day */}
            {dialogType === 'fullDay' && (
              <>
                <TextField
                  label="Date"
                  type="date"
                  required
                  size="small"
                  value={fullDayDate}
                  onChange={(e) => setFullDayDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: today }}
                  fullWidth
                />
                <TextField
                  label="Reason (optional)"
                  size="small"
                  value={fullDayReason}
                  onChange={(e) => setFullDayReason(e.target.value)}
                  multiline
                  rows={2}
                  fullWidth
                />
              </>
            )}

            {/* Custom Slot */}
            {dialogType === 'customSlot' && (
              <>
                <TextField
                  label="Date"
                  required
                  type="date"
                  size="small"
                  value={customSlotDate}
                  onChange={(e) => setCustomSlotDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: today }}
                  fullWidth
                />
                <TextField
                  label="Start Time"
                  required
                  type="time"
                  size="small"
                  value={customSlotStartTime}
                  onChange={(e) => setCustomSlotStartTime(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  label="End Time"
                  required
                  type="time"
                  size="small"
                  value={customSlotEndTime}
                  onChange={(e) => setCustomSlotEndTime(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={handleCloseDialog} variant="outlined" color="inherit" size="small">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            size="small"
            className={styles.addBtn}
            disabled={
              dialogType === 'fullDay'
                ? !fullDayDate || !doctorData.clinicId || !doctorData.doctorId
                : !customSlotDate ||
                  !customSlotStartTime ||
                  !customSlotEndTime ||
                  !doctorData.clinicId ||
                  !doctorData.doctorId
            }
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
      <DeleteDialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        onConfirm={confirmDelete}
        compact
      />
        </>
      )}
    </Box>
  );
}
